import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AdminService } from 'src/app/business-service/admin/admin.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';

@Component({
  selector: 'app-subscription-selection',
  templateUrl: './subscription-selection.component.html',
  styleUrls: ['./subscription-selection.component.scss'],
})
export class SubscriptionSelectionComponent implements OnInit {

  @Input() organizationId: any;
  @Input() editOrganizationId: any;
  @Input() storedFormData: any;

  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter<any>();
  @Output() submitOrganizationData: EventEmitter<void> = new EventEmitter<void>();

  allPackagesDetails: any[] = [];
  websiteBuilderPackages: any[] = [];
  selectedPackages: { crm?: any, websiteBuilder?: any } = {};

  packageForm!: FormGroup;

  @Input() isUserEditing: boolean = false;

  constructor(
    private _adminService: AdminService,
    private _globalService: GlobalService,
    private _fb: FormBuilder
  ) { }

  async ngOnInit() {
    await this.loadAllSubscriptionPackages();

    this.packageForm = this._fb.group({
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      packageIds: [[], [Validators.required, this.atLeastOnePackageValidator.bind(this)]]
    });

    this.packageForm.valueChanges.subscribe(() => {
      this.packageForm.markAsTouched();
    });

    if (this.storedFormData) {
      this.patchPackageData(this.storedFormData);
    }

    if (this.isUserEditing) {
      this.packageForm.get("startDate")?.disable();
      this.packageForm.get("endDate")?.disable();
    }
  }

  // Custom validator to ensure at least one package is selected
  atLeastOnePackageValidator(control: any) {
    return (control.value && control.value.length > 0) ? null : { required: true };
  }

  convertToDate(date:any): Date | null {
    if(!date){
      return null;
    }
    return new Date(date);
  }

  patchPackageData(formData: any) {
    this.packageForm.patchValue({
      ...formData,
      packageIds: formData.packageIds || []
    });

    // Set selectedPackages for UI
    this.selectedPackages = {};
    if (formData.packageIds && Array.isArray(formData.packageIds)) {
      for (const id of formData.packageIds) {
        if (this.websiteBuilderPackages.some(pkg => pkg.packageId === id)) {
          this.selectedPackages.websiteBuilder = this.websiteBuilderPackages.find(pkg => pkg.packageId === id);
        } else {
          this.selectedPackages.crm = this.allPackagesDetails.find(pkg => pkg.packageId === id);
        }
      }
    }
  }

  async loadAllSubscriptionPackages(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._adminService.getAllSubscriptionPackages().subscribe({
        next: (res: any) => {
          this.allPackagesDetails = res.data.filter((pkg: any) => pkg.packageId !== "PACK1006");
          this.websiteBuilderPackages = res.data.filter((pkg: any) => pkg.packageId === "PACK1006");
          resolve();
        },
        error: (error: any) => {
          console.error("Error while fetching subscription packages", error);
          reject(error);
        }
      });
    });
  }

 selectPackage(selected: any, type: 'crm' | 'websiteBuilder') {
    // Unselect if already selected
    if (this.selectedPackages[type] === selected) {
      this.selectedPackages[type] = undefined;
    } else {
      this.selectedPackages[type] = selected;
    }

    // Only allow one selection per type
    const packageIds: string[] = [];
    if (this.selectedPackages.crm) packageIds.push(this.selectedPackages.crm.packageId);
    if (this.selectedPackages.websiteBuilder) packageIds.push(this.selectedPackages.websiteBuilder.packageId);

    this.packageForm.get('packageIds')?.setValue(packageIds);
    this.packageForm.markAsTouched();
    this.packageForm.markAsDirty();
  }

  async onSubmit(): Promise<boolean> {
    if (this.packageForm.valid) {
      try {
        const formData = this.packageForm.getRawValue();
        // FIX: prevent timezone shift on startDate
        if (formData.startDate) {
          const d = new Date(formData.startDate);
          formData.startDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00.000Z`;
        }
        if (formData.endDate) {
          const d = new Date(formData.endDate);
          formData.endDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00.000Z`;
        }
        this.packageForm.markAsPristine();
        this.storeFormData.emit(formData);
        this.submitOrganizationData.emit();
        return true;
      } catch (error) {
        console.error("Error while adding visa info", error);
        return false;
      }
    } else {
      this.packageForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert("Select at least one Package to continue", "Package not selected");
      return false;
    }
  }
  

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  goPrevious() {
    this.viewChange.emit(true);
  }

  goNext() {
    this.viewChange.emit(false);
  }

  async saveAndNext() {
    if (await this.onSubmit()) {
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.packageForm.dirty || this.packageForm.invalid) && this.packageForm.touched;
  }

}
